import React, { useState, useEffect, useMemo } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps } from "./types";

type SortDirection = "asc" | "desc";
type ChartNameSortField = "chartname" | "invocation_count" | "play_count" | "conversion_rate" | "percentage_of_total";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const BC: any = BarChart as any;
const BR: any = Bar as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;
const CL: any = Cell as any;

/* eslint-disable camelcase */
type ChartNameDataPoint = {
    chartname: string;
    invocation_count: number;
    play_count: number;
    conversion_rate: number;
    percentage_of_total: number;
};
/* eslint-enable camelcase */

// Color palette for bars
const COLORS = [
    "#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8b5cf6",
    "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#6366f1",
    "#a855f7", "#ec4899", "#14b8a6", "#f97316", "#84cc16",
    "#22d3d8", "#8b5cf6", "#f472b6", "#4ade80", "#facc15"
];

/**
 * Custom tooltip showing all metrics
 */
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div style={{ 
                backgroundColor: "white", 
                padding: "10px", 
                border: "1px solid #ccc",
                borderRadius: "4px"
            }}>
                <p style={{ margin: 0, fontWeight: "bold" }}>{label}</p>
                <p style={{ margin: "5px 0 0 0" }}>
                    Invocations: {data.invocation_count.toLocaleString()}
                </p>
                <p style={{ margin: "5px 0 0 0" }}>
                    Plays: {data.play_count.toLocaleString()}
                </p>
                <p style={{ margin: "5px 0 0 0" }}>
                    Conversion Rate: {data.conversion_rate}%
                </p>
                <p style={{ margin: "5px 0 0 0" }}>
                    % of Total: {data.percentage_of_total}%
                </p>
            </div>
        );
    }
    return null;
};

/**
 * Chart Name Usage Widget - Usage distribution by recommendation chart name
 */
const ChartNameUsageWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions: _chartOptions
}) => {
    const [analyticsData, setAnalyticsData] = useState<ChartNameDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<ChartNameSortField>("invocation_count");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const sortedData = useMemo(() => {
        return [...analyticsData].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortDirection === "asc" 
                    ? aVal.localeCompare(bVal) 
                    : bVal.localeCompare(aVal);
            }
            return sortDirection === "asc" 
                ? (aVal as number) - (bVal as number) 
                : (bVal as number) - (aVal as number);
        });
    }, [analyticsData, sortField, sortDirection]);

    const handleSort = (field: ChartNameSortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const SortIcon = ({ field }: { field: ChartNameSortField }) => (
        <span style={{ marginLeft: "5px", opacity: sortField === field ? 1 : 0.3 }}>
            {sortField === field && sortDirection === "asc" ? "▲" : "▼"}
        </span>
    );

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "chartname_usage",
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandId,
                    filters.deviceType
                );
                
                const data = (response as any)?.result;
                const dataArray = Array.isArray(data) ? data : [];
                setAnalyticsData(dataArray);
            } catch (err) {
                Logger.error("Error fetching chart name usage data", err);
                setError("Failed to fetch chart name usage data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters]);

    if (isLoading) {
        return (
            <div className="text-center" style={{ padding: "40px" }}>
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ marginBottom: "40px" }}>
                <h4>Chart Name Usage</h4>
                <Alert variant="danger">{error}</Alert>
            </div>
        );
    }

    // Calculate chart height based on number of data points (min 300, max 700)
    const chartHeight = Math.min(700, Math.max(300, analyticsData.length * 35));

    // Calculate totals for summary
    const totalInvocations = analyticsData.reduce((sum, d) => sum + d.invocation_count, 0);
    const totalPlays = analyticsData.reduce((sum, d) => sum + d.play_count, 0);

    return (
        <div style={{ marginBottom: "40px" }}>
            <h4>Chart Name Usage</h4>
            
            {analyticsData.length === 0 ? (
                <Alert variant="info">No data available for the selected date range.</Alert>
            ) : (
                <>
                    {/* Summary stats */}
                    <div style={{ 
                        display: "flex", 
                        gap: "20px", 
                        marginBottom: "15px",
                        flexWrap: "wrap"
                    }}>
                        <div style={{ 
                            backgroundColor: theme.chartBg, 
                            padding: "15px 20px", 
                            borderRadius: "5px",
                            minWidth: "150px"
                        }}>
                            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#8884d8" }}>
                                {analyticsData.length}
                            </div>
                            <div style={{ color: "#666" }}>Unique Chart Names</div>
                        </div>
                        <div style={{ 
                            backgroundColor: theme.chartBg, 
                            padding: "15px 20px", 
                            borderRadius: "5px",
                            minWidth: "150px"
                        }}>
                            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#82ca9d" }}>
                                {totalInvocations.toLocaleString()}
                            </div>
                            <div style={{ color: "#666" }}>Total Invocations</div>
                        </div>
                        <div style={{ 
                            backgroundColor: theme.chartBg, 
                            padding: "15px 20px", 
                            borderRadius: "5px",
                            minWidth: "150px"
                        }}>
                            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ffc658" }}>
                                {totalPlays.toLocaleString()}
                            </div>
                            <div style={{ color: "#666" }}>Total Plays</div>
                        </div>
                    </div>

                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "20px", 
                        borderRadius: "5px",
                        marginBottom: "20px"
                    }}>
                        <RESP width="100%" height={chartHeight}>
                            <BC 
                                data={analyticsData} 
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                            >
                                <CG strokeDasharray="3 3" />
                                <XA type="number" />
                                <YA 
                                    type="category" 
                                    dataKey="chartname" 
                                    width={140}
                                    tick={{ fontSize: 11 }}
                                />
                                <TT content={<CustomTooltip />} />
                                <LG />
                                <BR 
                                    dataKey="invocation_count" 
                                    name="Invocations"
                                    fill="#8884d8"
                                >
                                    {analyticsData.map((_entry, index) => (
                                        <CL key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </BR>
                            </BC>
                        </RESP>
                    </div>

                    {/* Summary table */}
                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "15px", 
                        borderRadius: "5px",
                        marginBottom: "20px",
                        overflowX: "auto"
                    }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "2px solid #ddd" }}>
                                    <th 
                                        style={{ padding: "10px", textAlign: "left", cursor: "pointer" }}
                                        onClick={() => handleSort("chartname")}
                                    >
                                        Chart Name<SortIcon field="chartname" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("invocation_count")}
                                    >
                                        Invocations<SortIcon field="invocation_count" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("play_count")}
                                    >
                                        Plays<SortIcon field="play_count" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("conversion_rate")}
                                    >
                                        Conversion Rate<SortIcon field="conversion_rate" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("percentage_of_total")}
                                    >
                                        % of Total<SortIcon field="percentage_of_total" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((row, idx) => (
                                    <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "10px" }}>{row.chartname}</td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.invocation_count.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.play_count.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.conversion_rate}%
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.percentage_of_total}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{ 
                            backgroundColor: "#f5f5f5", 
                            padding: "15px", 
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(analyticsData, null, 2)}
                        </pre>
                    </details>
                </>
            )}
        </div>
    );
};

export default ChartNameUsageWidget;
